import { ChangeEvent, ReactNode, useEffect, useState } from 'react'
import { isEqual } from 'lodash'

import { Option as ArchidektDropdownOption } from 'components/elements/ArchidektDropdown/types'

import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import Icon from 'components/elements/Icon'

import styles from './multiSelectDropdown.module.scss'

type Option = { label: ReactNode; value: any; searchString?: string }

type Props = {
  value?: any[]
  options: Option[]
  onChange: (values: any[]) => void
  placeholder?: string
  disabled?: boolean
  header?: ReactNode
  searchable?: boolean
  className?: string
}

const MultiSelectDropdown = ({
  value = [],
  onChange,
  options,
  placeholder = 'Select options',
  disabled = false,
  header,
  searchable,
  className,
}: Props) => {
  const [localValue, setLocalValue] = useState(new Array<Option>())
  const [searchValue, setSearchValue] = useState('')

  useEffect(() => {
    // If the arrays are equal, there's no change neccesary
    if (
      isEqual(
        localValue.map(opt => opt.value),
        value,
      )
    )
      return

    const mappedValues = value.reduce((acc, value) => {
      const matchedOption = options.find(opt => opt.value === value)

      if (matchedOption) acc.push(matchedOption)

      return acc
    }, [] as Option[])

    setLocalValue(mappedValues)
  }, [value])

  const handleUpdateLocalValues = (updatedValues: Option[]) => {
    setSearchValue('')
    setLocalValue(updatedValues)

    if (onChange) onChange(updatedValues.map(opt => opt.value))
  }

  const handleChange = (option: Option) => handleUpdateLocalValues([...localValue, option])
  const handleRemove = (option: Option) => handleUpdateLocalValues(localValue.filter(opt => opt.value !== option.value))

  const selectedValuesOnly = localValue.map(opt => opt.value)
  const localOptions: ArchidektDropdownOption[] = options.reduce((acc, opt) => {
    if (selectedValuesOnly.includes(opt.value)) return acc
    if (searchValue && !opt.searchString?.toLocaleLowerCase()?.includes(searchValue.toLocaleLowerCase())) return acc

    acc.push({
      label: opt.label,
      onClick: () => handleChange(opt),
      noCloseOnClick: true,
    })

    return acc
  }, new Array<ArchidektDropdownOption>())

  if (!localOptions.length) localOptions.push({ label: 'No results found', onClick: () => null, disabled: true })

  return (
    <ArchidektDropdown
      skipCloseOnClick
      options={localOptions}
      menuClassName={styles.menu}
      triggerClassName={`${styles.containmer} ${className}`}
      disabled={disabled}>
      {header && <label className={styles.header}>{header}</label>}

      <div className={`${styles.fauxInput} ${disabled ? styles.disabled : ''}`}>
        {localValue.map((opt, index) => (
          <button
            key={opt.value}
            onClick={() => handleRemove(opt)}
            className={`${styles.selectedOption} ${index === 0 ? styles.marginLeft : ''}`}
            disabled={disabled}>
            {opt.label}
            <Icon name="close" />
          </button>
        ))}

        <input
          value={searchValue}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
          disabled={disabled || !searchable}
          className={styles.input}
          placeholder={localValue.length ? '' : placeholder}
        />
      </div>
    </ArchidektDropdown>
  )
}

export default MultiSelectDropdown
